import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { musicalWorkService } from "../services/musicalWorkService";
import {
  MusicalWorkValuesInit,
  getMusicalWorkUpdateVariables,
  getMusicalWorkValues,
  MusicalWorkValuesType,
  MusicalWorkErrorsType,
  musicalWorkDisplayId,
} from "./MusicalWorkCommonStore";
import { validateAuthorPercentages } from "./customValidations";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { MusicalWork, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import { processTerritoriesSelection, optimizeTerritoriesForSave, validateTerritoriesExclusivity } from "../../territory/utils/territorySelectionHelper";
import {
  getMusicalGenreValues,
  MusicalGenreValuesType,
  musicalGenreDisplayId,
} from "../../musicalGenre/store/MusicalGenreCommonStore";
import { WorkAuthorUpdateStore } from "../../workAuthor/store";
import {
  getInterpreterValues,
  InterpreterValuesType,
  interpreterDisplayId,
} from "../../interpreter/store/InterpreterCommonStore";
import {
  getTerritoryValues,
  TerritoryValuesType,
  territoryDisplayId,
} from "../../territory/store/TerritoryCommonStore";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { PhonogramUpdateStore } from "../../phonogram/store";
import { uploadFailureReason } from "../../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { GridChangeSet } from "../../common/types";
import { confirm } from "../../../common/services/confirmService";
import { getApiAsync } from "../../../api";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { actualStatusList } from "../../common/types";
import { musicalGenreService } from "../../musicalGenre/services/musicalGenreService";
import { interpreterService } from "../../interpreter/services/interpreterService";
import { territoryService } from "../../territory/services/territoryService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class MusicalWorkUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: MusicalWorkErrorsType = {};
  service = musicalWorkService;
  musicalWorkInstance: MusicalWork | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    workInfo: { open: true, errors: [] },
    workRegistration: { open: true, errors: [] },
    workContent: { open: true, errors: [] },
    contracts: { open: true, errors: [] },
    systemDates: { open: false, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = MusicalWorkValuesInit();
  titleError: string | undefined;
  musicalGenreError: string | undefined;
  musicalGenreService = musicalGenreService;
  musicalGenreSearchValue: string = "";
  musicalGenreOptions: MusicalGenreValuesType[] = [];
  alternativeTitlesError: string | undefined;
  authorsError: string | undefined;
  authorsVisibleColumns: { name: string; title: string }[];
  authorsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  authorsUpdateStore: WorkAuthorUpdateStore;
  notesError: string | undefined;
  performersError: string | undefined;
  performersService = interpreterService;
  performersSearchValue: string = "";
  performersOptions: InterpreterValuesType[] = [];
  cmsIdError: string | undefined;
  territoriesError: string | undefined;
  territoriesService = territoryService;
  territoriesSearchValue: string = "";
  territoriesOptions: TerritoryValuesType[] = [];
  iswcError: string | undefined;
  scoreError: string | undefined;
  phonogramsError: string | undefined;
  phonogramsVisibleColumns: { name: string; title: string }[];
  phonogramsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  phonogramsUpdateStore: PhonogramUpdateStore;
  originalLyricsError: string | undefined;
  contractObservationError: string | undefined;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<MusicalWorkValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.authorsVisibleColumns = [
      { name: "__author", title: this.langStore.WorkAuthorAuthorLabel },
      {
        name: "lyricsPercentage",
        title: this.langStore.WorkAuthorLyricsPercentageLabel,
      },
      {
        name: "musicPercentage",
        title: this.langStore.WorkAuthorMusicPercentageLabel,
      },
    ];
    this.authorsColumnTypes = {
      __author: {
        fieldType: "__Reference" as const,
        nullable: false,
        readOnly: false,
        multi: false,
        onChange: (value: string) =>
          this.authorsUpdateStore.setAuthorValue(value),
        onSelect: (value: any) =>
          this.authorsUpdateStore.setSelectedAuthor(value),
        getOptions: () => this.authorsUpdateStore.getAuthorOptions(),
      },
      lyricsPercentage: {
        fieldType: "Decimal" as const,
        nullable: false,
        readOnly: false,
      },
      musicPercentage: {
        fieldType: "Decimal" as const,
        nullable: false,
        readOnly: false,
      },
    };
    this.authorsUpdateStore = new WorkAuthorUpdateStore(
      rootStore,
      "authors_" + this.storeId
    );
    this.phonogramsVisibleColumns = [
      { name: "audioFile", title: this.langStore.PhonogramAudioFileLabel },
      { name: "trackName", title: this.langStore.PhonogramTrackNameLabel },
      { name: "isrc", title: this.langStore.PhonogramIsrcLabel },
      { name: "albumName", title: this.langStore.PhonogramAlbumNameLabel },
      {
        name: "publicationYear",
        title: this.langStore.PhonogramPublicationYearLabel,
      },
    ];
    this.phonogramsColumnTypes = {
      audioFile: {
        fieldType: "File" as const,
        nullable: false,
        readOnly: false,
      },
      trackName: {
        fieldType: "String" as const,
        nullable: false,
        readOnly: false,
      },
      isrc: { fieldType: "String" as const, nullable: true, readOnly: false },
      albumName: {
        fieldType: "String" as const,
        nullable: true,
        readOnly: false,
      },
      publicationYear: {
        fieldType: "Int" as const,
        nullable: true,
        readOnly: false,
      },
    };
    this.phonogramsUpdateStore = new PhonogramUpdateStore(
      rootStore,
      "phonograms_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<MusicalWorkValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }

    if (this.authorsUpdateStore) {
      this.authorsUpdateStore.destroy();
    }
    if (this.phonogramsUpdateStore) {
      this.phonogramsUpdateStore.destroy();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<MusicalWorkValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadMusicalWorkById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.musicalWorkInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.musicalWorkInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getMusicalWorkValues(
        this.musicalWorkInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadMusicalWork(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadMusicalWorkById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return musicalWorkDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.musicalWorkInstance) {
      return musicalWorkDisplayId(this.musicalWorkInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.MusicalWorkSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "workInfo",
        label: this.rootStore.langStore.groupMusicalWorkWorkInfoLabel,
        errors: this.getCardErrors("workInfo"),
      },
      {
        id: "workRegistration",
        label: this.rootStore.langStore.groupMusicalWorkWorkRegistrationLabel,
        errors: this.getCardErrors("workRegistration"),
      },
      {
        id: "workContent",
        label: this.rootStore.langStore.groupMusicalWorkWorkContentLabel,
        errors: this.getCardErrors("workContent"),
      },
      {
        id: "contracts",
        label: this.rootStore.langStore.groupMusicalWorkContractsLabel,
        errors: this.getCardErrors("contracts"),
      },
      {
        id: "systemDates",
        label: this.rootStore.langStore.groupMusicalWorkSystemDatesLabel,
        errors: this.getCardErrors("systemDates"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity
      if (
        ["WorkAuthor", "WorkAuthorPublisher"].includes(
          callerData?.entityName as string
        )
      ) {
        this.authorsUpdateStore.isWaitingForCallback = false;
        this.authorsUpdateStore.callbackReturnPath = null;
        yield this.authorsUpdateStore.returnedData(data);
        return;
      }
      if (
        ["Phonogram", "MusicalWorkLink"].includes(
          callerData?.entityName as string
        )
      ) {
        this.phonogramsUpdateStore.isWaitingForCallback = false;
        this.phonogramsUpdateStore.callbackReturnPath = null;
        yield this.phonogramsUpdateStore.returnedData(data);
        return;
      }

      if (callerData?.fieldName === "musicalGenre") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            musicalGenreDisplayId(returnData.entity, this.langStore);
          this.entityValues.showMusicalGenre = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.musicalGenreOne({ id: returnData.resultId });
          const musicalGenre = result.musicalGenreOne;
          this.entityValues.showMusicalGenre = {
            displayId: musicalGenre.displayId,
            id: musicalGenre.id,
          };
          this.setMusicalGenreOptions([
            getMusicalGenreValues(musicalGenre, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "performers") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            interpreterDisplayId(returnData.entity, this.langStore);
          this.entityValues.showPerformers.push({
            id: returnData.entity.id,
            displayId,
          });
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.interpreterOne({ id: returnData.resultId });
          const interpreter = result.interpreterOne;
          this.entityValues.showPerformers.push({
            displayId: interpreter.displayId,
            id: interpreter.id,
          });
          this.setPerformersOptions([
            getInterpreterValues(interpreter, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "territories") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            territoryDisplayId(returnData.entity, this.langStore);
          this.entityValues.showTerritories.push({
            id: returnData.entity.id,
            displayId,
          });
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.territoryOne({ id: returnData.resultId });
          const territory = result.territoryOne;
          this.entityValues.showTerritories.push({
            displayId: territory.displayId,
            id: territory.id,
          });
          this.setTerritoriesOptions([
            getTerritoryValues(territory, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getTitleValue(): string {
    return this.entityValues.title;
  }

  setTitleValue(value: string) {
    this.entityValues.title = value;
  }

  setMusicalGenreOptions(values: MusicalGenreValuesType[]) {
    this.musicalGenreOptions = values;
  }

  *loadMusicalGenreValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("MusicalGenre");
    const allOptions = cache.getOptions(
      "MusicalGenre"
    ) as MusicalGenreValuesType[];

    const searchValue = this.musicalGenreSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setMusicalGenreOptions(filtered);
  }

  getMusicalGenreOptions() {
    return this.musicalGenreOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setMusicalGenreValue(value: string) {
    this.musicalGenreSearchValue = value;
    yield this.loadMusicalGenreValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedMusicalGenre(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewMusicalGenre(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "musicalGenre"
      );
    } else {
      if (value) {
        this.entityValues.showMusicalGenre = value;
        this.entityValues.__musicalGenre = value.displayId;
      }
    }
  }

  getAlternativeTitlesValue(): string[] {
    return this.entityValues.alternativeTitles;
  }

  setAlternativeTitlesValue(value: string[]) {
    this.entityValues.alternativeTitles = value;
  }

  private get authorshasChanges() {
    return (
      this.entityValues.showAuthors?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  get authorsFooter() {
    let totalLyricsPercentage = 0;
    let totalMusicPercentage = 0;
    for (const workAuthor of this.entityValues.showAuthors) {
      totalLyricsPercentage += Number(workAuthor.lyricsPercentage) || 0;
      totalMusicPercentage += Number(workAuthor.musicPercentage) || 0;
    }

    const formatPercentage = (value: number) => {
      const rounded = Math.round(value * 100) / 100;
      return rounded % 1 === 0 ? `${rounded}%` : `${rounded.toFixed(2)}%`;
    };

    const alert = {sx: { fontSize: 20, color: 'error.main', fontWeight: 'normal' }};
    const normal = {sx: { fontSize: 20, color: 'success.main', fontWeight: 'normal' }};
    return {
      lyricsPercentage: {value: formatPercentage(totalLyricsPercentage), ...(totalLyricsPercentage > 100 ? alert : normal)},
      musicPercentage: {value: formatPercentage(totalMusicPercentage), ...(totalMusicPercentage > 100 ? alert : normal)}
    }
  }

  authorsLoadRow(row: any) {
    this.authorsUpdateStore.loadValuesFromRow(row);
  }

  authorsRestore(id: string) {
    this.entityValues.showAuthors = this.entityValues.showAuthors.map((item) =>
      item.id === id
        ? { ...item, __status__: restoredStatus(item.__status__) }
        : item
    );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  authorsValidateChanges(_changes: GridChangeSet) {
    const errors = this.authorsUpdateStore.validateData(
      this.authorsUpdateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.authorsUpdateStore.entityValues;
    const existingRows = this.entityValues.showAuthors || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: author
    const isDuplicate_author = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.showAuthor?.id === currentValues.showAuthor?.id &&
        currentValues.showAuthor?.id
    );
    if (isDuplicate_author) {
      errors.author = {
        message: this.langStore.duplicateValueError,
        fieldLabel: this.langStore.WorkAuthorAuthorLabel,
      };
    }

    this.authorsUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  authorsCommitInlineChange(rowId: string, changes: Record<string, any>) {
    this.entityValues.showAuthors = this.entityValues.showAuthors.map((item) =>
      item.id === rowId
        ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
        : item
    );
  }

  authorsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.authorsUpdateStore.entityValues;
      const hadChanges = this.authorsUpdateStore.pendingChanges;
      if (hadChanges) {
        const authors = this.entityValues.showAuthors;
        for (
          let index = 0;
          index < this.entityValues.showAuthors.length;
          index++
        ) {
          if (authors[index].id === values.id) {
            this.entityValues.showAuthors = this.entityValues.showAuthors.map(
              (item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
            );
            break;
          }
        }
        this.authorsUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.authorsUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showAuthors) {
        this.entityValues.showAuthors = [];
      }
      this.entityValues.showAuthors = [
        ...this.entityValues.showAuthors,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.authorsUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const authors = this.entityValues.showAuthors;
      for (
        let index = 0;
        index < this.entityValues.showAuthors.length;
        index++
      ) {
        if (authors[index].id === id) {
          if (isOriginalCrudStatus(authors[index].__status__)) {
            this.entityValues.showAuthors = this.entityValues.showAuthors.map(
              (item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
            );
          } else {
            this.entityValues.showAuthors =
              this.entityValues.showAuthors.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.authors);
    //   this.setCRUDRowsFromValues(newChanges, this.authorsUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.authors = getNewCRUDRows(newChanges, this.entityValues.authors, this.uniqueCounter++);
    // }
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  setPerformersOptions(values: InterpreterValuesType[]) {
    this.performersOptions = values;
  }

  *loadPerformersValues(): any {
    let where: any = null;
    if (this.performersSearchValue) {
      const searchFilter = {
        name: { contains: this.performersSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.interpreterListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setPerformersOptions(
      (result.interpreterListWithOffset || []).map((x: any) =>
        getInterpreterValues(x, this.langStore)
      )
    );
  }

  getPerformersOptions() {
    return this.performersOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPerformersValue(value: string) {
    this.performersSearchValue = value;
    yield this.loadPerformersValues();
  }

  setSelectedPerformers(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewInterpreter(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "performers"
      );
    } else {
      this.entityValues.showPerformers = values;
      this.entityValues.__performers = values.map((v) => v.displayId);
    }
  }

  getCmsIdValue(): string {
    return this.entityValues.cmsId;
  }

  setCmsIdValue(value: string) {
    this.entityValues.cmsId = value;
  }

  setTerritoriesOptions(values: TerritoryValuesType[]) {
    this.territoriesOptions = values;
  }

  *loadTerritoriesValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Territory");
    const allOptions = cache.getOptions("Territory") as TerritoryValuesType[];

    const searchValue = this.territoriesSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setTerritoriesOptions(filtered);
  }

  getTerritoriesOptions() {
    return this.territoriesOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setTerritoriesValue(value: string) {
    this.territoriesSearchValue = value;
    yield this.loadTerritoriesValues();
  }

  setSelectedTerritories(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewTerritory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "territories"
      );
    } else {
      // Usar todas las opciones del cache para la lógica de exclusividad
      const cache = this.rootStore.referenceCacheStore;
      const allOptions = cache.getOptions("Territory") as TerritoryValuesType[];
      const processedValues = processTerritoriesSelection(
        values,
        this.entityValues.showTerritories,
        allOptions
      );
      this.entityValues.showTerritories = processedValues;
      this.entityValues.__territories = processedValues.map((v) => v.displayId);
    }
  }

  getIswcValue(): string {
    return this.entityValues.iswc;
  }

  setIswcValue(value: string) {
    this.entityValues.iswc = value;
  }

  scoreRestoreFile(id: string) {
    const file = this.entityValues.__score_files.find((x) => x.id === id);
    if (!file) return;

    const newStatus = restoredStatus(file.__status__);

    const activeFiles = this.entityValues.__score_files.filter((x) =>
      actualStatusList.includes(x.__status__)
    );
    if (activeFiles.length >= 1 && newStatus !== file.__status__) {
      alert("No puede haber más de un archivo activo");
      return;
    }

    file.__status__ = newStatus;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *scoreUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__score_files];
    const previousName = this.entityValues.score;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      this.scoreError = this.langStore.MusicalWorkScoreLabel + ": solo se permiten archivos PDF";
      return;
    }
    this.scoreError = undefined;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__score_files.push(newFile);
    this.entityValues.score = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__score_files = previousFiles;
      this.entityValues.score = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("score");
      } else {
        this.setFieldError(
          "score",
          this.langStore.MusicalWorkScoreLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("score");
    const fileIndex = this.entityValues.__score_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__score_files[fileIndex].ticket = outcome.ticket;
    }
  }

  scoreCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__score_files) {
        if (file.id === id) {
          if (isOriginalCrudStatus(file.__status__)) {
            file.__status__ = "original-deleted";
          } else {
            toRemove = index;
          }
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__score_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  private get phonogramshasChanges() {
    return (
      this.entityValues.showPhonograms?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  phonogramsLoadRow(row: any) {
    this.phonogramsUpdateStore.loadValuesFromRow(row);
  }

  phonogramsRestore(id: string) {
    this.entityValues.showPhonograms = this.entityValues.showPhonograms.map(
      (item) =>
        item.id === id
          ? { ...item, __status__: restoredStatus(item.__status__) }
          : item
    );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  phonogramsValidateChanges(_changes: GridChangeSet) {
    const errors = this.phonogramsUpdateStore.validateData(
      this.phonogramsUpdateStore.entityValues
    );

    this.phonogramsUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  phonogramsCommitInlineChange(rowId: string, changes: Record<string, any>) {
    this.entityValues.showPhonograms = this.entityValues.showPhonograms.map(
      (item) =>
        item.id === rowId
          ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
          : item
    );
  }

  phonogramsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.phonogramsUpdateStore.entityValues;
      const hadChanges = this.phonogramsUpdateStore.pendingChanges;
      if (hadChanges) {
        const phonograms = this.entityValues.showPhonograms;
        for (
          let index = 0;
          index < this.entityValues.showPhonograms.length;
          index++
        ) {
          if (phonograms[index].id === values.id) {
            this.entityValues.showPhonograms =
              this.entityValues.showPhonograms.map((item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
              );
            break;
          }
        }
        this.phonogramsUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.phonogramsUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showPhonograms) {
        this.entityValues.showPhonograms = [];
      }
      this.entityValues.showPhonograms = [
        ...this.entityValues.showPhonograms,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.phonogramsUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const phonograms = this.entityValues.showPhonograms;
      for (
        let index = 0;
        index < this.entityValues.showPhonograms.length;
        index++
      ) {
        if (phonograms[index].id === id) {
          if (isOriginalCrudStatus(phonograms[index].__status__)) {
            this.entityValues.showPhonograms =
              this.entityValues.showPhonograms.map((item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
              );
          } else {
            this.entityValues.showPhonograms =
              this.entityValues.showPhonograms.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.phonograms);
    //   this.setCRUDRowsFromValues(newChanges, this.phonogramsUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.phonograms = getNewCRUDRows(newChanges, this.entityValues.phonograms, this.uniqueCounter++);
    // }
  }

  getOriginalLyricsValue(): string {
    return this.entityValues.originalLyrics;
  }

  setOriginalLyricsValue(value: string) {
    this.entityValues.originalLyrics = value;
  }

  getContractObservationValue(): string {
    return this.entityValues.contractObservation;
  }

  setContractObservationValue(value: string) {
    this.entityValues.contractObservation = value;
  }

  getCreatedAt_Value(): Date | null {
    return this.entityValues.createdAt_;
  }

  getLastUpdatedAt_Value(): Date | null {
    return this.entityValues.lastUpdatedAt_;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as MusicalWorkErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as MusicalWorkErrorsType;
  }

  *fileDownload(props: any, fieldName: string): any {
    // En el modal anidado (subentidad editada desde el parent) el instance
    // no se carga (loadValuesFromRow): el id sale de los values de la fila.
    // Sin el fallback, la URL iba con instanceId=undefined → 404 silencioso.
    const instanceId = this.musicalWorkInstance?.id ?? this.entityValues.id;
    yield downloadWithAuth(
      {
        entityName: "MusicalWork",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      },
      undefined,
      this.rootStore.activityStore
    );
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      // Mismo fallback que fileDownload: en el modal anidado el instance
      // no se carga y el id viene de los values de la fila.
      const instanceId = this.musicalWorkInstance?.id ?? this.entityValues.id;
      const blob = yield fetchFileBlob({
        entityName: "MusicalWork",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  *addNewMusicalGenre(
    notFound: string | undefined,
    callerFieldName?: string
  ): any {
    // QuickCreate checks for fields with quickCreate enabled
    if (notFound && callerFieldName === "musicalGenre") {
      const confirmed = yield confirm(
        this.langStore.quickCreateConfirmTitle(
          this.langStore.MusicalGenreSingular
        ),
        this.langStore.quickCreateConfirmDescription(
          notFound,
          this.langStore.MusicalGenreSingular
        )
      );
      if (!confirmed) return;

      try {
        const api = yield getApiAsync(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.musicalGenreCreate({ name: notFound });

        if (result.musicalGenreCreate) {
          this.entityValues.showMusicalGenre = {
            id: result.musicalGenreCreate.id,
            displayId: result.musicalGenreCreate.displayId || notFound,
          };
          this.entityValues.__musicalGenre =
            result.musicalGenreCreate.displayId || notFound;
        }
      } catch (error) {
        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      }
      return;
    }

    // Fallback to navigation for other fields
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWork",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/musicalGenre/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewInterpreter(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWork",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/interpreter/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewTerritory(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWork",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/territory/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.musicalWorkInstance = null;
    this.entityValues = MusicalWorkValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      workInfo: { open: true, errors: [] },
      workRegistration: { open: true, errors: [] },
      workContent: { open: true, errors: [] },
      contracts: { open: true, errors: [] },
      systemDates: { open: false, errors: [] },
    };
    this.musicalGenreSearchValue = "";
    this.musicalGenreOptions = [];
    this.performersSearchValue = "";
    this.performersOptions = [];
    this.territoriesSearchValue = "";
    this.territoriesOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: MusicalWorkValuesType): MusicalWorkErrorsType {
    const errors: MusicalWorkErrorsType = {};
    this.clearErrors();
    values.title = values.title.trim();
    if (values.title === "") {
      errors.title = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.MusicalWorkTitleLabel,
      };
      this.setCardError("workInfo", errors.title.message);
    }
    this.musicalGenreError = undefined;
    if (values.showMusicalGenre === null || values.showMusicalGenre.id === "") {
      errors.musicalGenre = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.MusicalWorkMusicalGenreLabel,
      };
      this.setCardError("workInfo", errors.musicalGenre.message);
    }
    if (values.showAuthors.length === 0) {
      errors.authors = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.MusicalWorkAuthorsLabel,
      };
      this.setCardError("workInfo", errors.authors.message);
    }
    if (values.showTerritories.length === 0) {
      errors.territories = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.MusicalWorkTerritoriesLabel,
      };
      this.setCardError("workRegistration", errors.territories.message);
    }
    {
      const pendingUpload = values.__score_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.score = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.MusicalWorkScoreLabel,
        };
        this.setCardError("workContent", errors.score.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "MusicalWorkUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges || this.hasArrayChanges();
  }

  private hasArrayChanges(): boolean {
    return this.authorshasChanges || this.phonogramshasChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      title: this.entityValues.title,
      alternativeTitles: this.entityValues.alternativeTitles,
      notes: this.entityValues.notes,
      cmsId: this.entityValues.cmsId,
      iswc: this.entityValues.iswc,
      score: this.entityValues.score,
      originalLyrics: this.entityValues.originalLyrics,
      contractObservation: this.entityValues.contractObservation,
      musicalGenre: this.entityValues.showMusicalGenre?.id || null,
      performers: (this.entityValues.showPerformers || [])
        .map((x: any) => x.id)
        .sort(),
      territories: (this.entityValues.showTerritories || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }
    if (this.authorshasChanges) changed.push("authors");
    if (this.phonogramshasChanges) changed.push("phonograms");
    return changed;
  }

  *saveMusicalWork(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadMusicalWorkById(this.savedId);
              } catch (error) {
                console.warn(
                  "[MusicalWorkUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelMusicalWork(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getMusicalWorkValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = MusicalWorkValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.musicalWorkInstance) {
      const cache = this.rootStore.referenceCacheStore;
      const allTerritoryOptions = cache.getOptions("Territory") as TerritoryValuesType[];

      // Validar exclusividad de "Todo el mundo"
      const territoryError = validateTerritoriesExclusivity(
        this.entityValues.showTerritories,
        allTerritoryOptions
      );
      if (territoryError) {
        throw new Error(territoryError);
      }

      // Optimizar territorios: si todos los países están seleccionados → solo "Todo el mundo"
      const optimizedTerritories = optimizeTerritoriesForSave(
        this.entityValues.showTerritories,
        allTerritoryOptions
      );
      const entityValuesForSave = {
        ...this.entityValues,
        showTerritories: optimizedTerritories,
      };

      const variables = getMusicalWorkUpdateVariables(entityValuesForSave);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.musicalWorkUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.musicalWorkUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.musicalWorkUpdate.version_;
      this.saveAttempt = null;
      this.musicalWorkInstance = result.musicalWorkUpdate as MusicalWork;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      // Register custom validators
      this.registerCustomValidator(validateAuthorPercentages);

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadMusicalWorkById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadMusicalGenreValues();
      yield this.loadPerformersValues();
      yield this.loadTerritoriesValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
